/**
 * @fileoverview Hook para preview do disparo em massa: dado um objeto de filtros,
 * consulta GET /contacts/preview e retorna { total, sample, loading }.
 * Debounce 500ms pra não bater a API a cada keystroke.
 */

import { useEffect, useRef, useState } from "react";
import api from "../../services/api";

const DEBOUNCE_MS = 500;

// Serializa o objeto para detectar mudança real (evita re-fetch com mesma ref diferente)
const stableKey = (filter) => {
  try {
    return JSON.stringify(filter || {});
  } catch {
    return "";
  }
};

/**
 * @param {object} filter  objeto com nameContains, numberContains, emailContains,
 *                         createdFrom/To, lastInteractionFrom/To, excludeGroups,
 *                         excludeReceivedTemplateSince: { templateName, days }
 * @param {boolean} enabled  se false, não dispara fetch (ex: modo não é "filter")
 */
const useContactPreview = (filter, enabled = true) => {
  const [total, setTotal] = useState(0);
  const [sample, setSample] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const key = stableKey(filter);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setTotal(0);
      setSample([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      // Cancela requisição anterior se ainda em voo
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);

      try {
        // Monta params achatando dedup aninhado pra query string
        const params = { ...(filter || {}) };
        const dedup = params.excludeReceivedTemplateSince;
        delete params.excludeReceivedTemplateSince;
        if (dedup && dedup.templateName && Number(dedup.days) > 0) {
          params["excludeReceivedTemplateSince[templateName]"] = dedup.templateName;
          params["excludeReceivedTemplateSince[days]"] = dedup.days;
        }

        const { data } = await api.get("/contacts/preview", {
          params,
          signal: controller.signal
        });
        setTotal(data.total || 0);
        setSample(Array.isArray(data.sample) ? data.sample : []);
      } catch (err) {
        // Abort não é erro real
        if (err?.name === "CanceledError" || err?.name === "AbortError") return;
        setError(err);
        setTotal(0);
        setSample([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return { total, sample, loading, error };
};

export default useContactPreview;
